import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useRequest, type QueryFunctionOptions } from '@frontend/api';

import type {
  DxfMappingValidationResult,
  DxfSceneMappingWritePayload,
} from './types';

export type DxfSceneMappingAssignments =
  DxfSceneMappingWritePayload['assignments'];

export const VALIDATE_DXF_SCENE_MAPPING_SCOPE = [
  'dxfSceneMappingValidation',
] as const;

export type ValidateDxfSceneMappingQueryKeyType = readonly [
  'dxfSceneMappingValidation',
  string,
  string,
];

export const getValidateDxfSceneMappingQueryKey = (
  partFileUuid: string,
  draftKey: string,
): ValidateDxfSceneMappingQueryKeyType =>
  [...VALIDATE_DXF_SCENE_MAPPING_SCOPE, partFileUuid, draftKey] as const;

/**
 * Query for the validation of a draft mapping. Takes a "draft key" rather than the assignments themselves,
 * to avoid re-hashing large mapping objects
 */
export const useValidateDxfSceneMapping = (
  {
    partFileUuid,
    draftKey,
    assignments,
  }: {
    partFileUuid: string;
    draftKey: string;
    assignments: DxfSceneMappingAssignments;
  },
  {
    requestOptions,
    queryOptions,
  }: QueryFunctionOptions<
    DxfMappingValidationResult,
    ValidateDxfSceneMappingQueryKeyType
  > = {},
) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getValidateDxfSceneMappingQueryKey(partFileUuid, draftKey),
    queryFn: ({ signal }) =>
      request<DxfMappingValidationResult>(
        `/dxf/${partFileUuid}/mapping/validate`,
        {
          init: {
            method: 'POST',
            body: { assignments, partFileUuid },
            signal,
          },
        },
      ),
  });
};

/**
 * Aborts any validation in flight for this part file.
 */
export const useCancelValidateDxfSceneMapping = (partFileUuid: string) => {
  const queryClient = useQueryClient();

  return useCallback(
    () =>
      queryClient.cancelQueries({
        queryKey: [...VALIDATE_DXF_SCENE_MAPPING_SCOPE, partFileUuid],
      }),
    [queryClient, partFileUuid],
  );
};
